import { CloseCircle, SearchNormal1 } from 'iconsax-react';
import { type ChangeEventHandler, type FocusEventHandler, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '@/utils/merge-class-names';

type Props = {
  keyword: string;
  setKeyword?: (keyword: string) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  clearKeyword?: () => void;
  icon?: boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  inputClassName?: string;
};

const SearchInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      keyword,
      placeholder,
      setKeyword,
      clearKeyword,
      onChange,
      onFocus,
      onBlur,
      icon = true,
      className,
      autoFocus,
      inputClassName,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    return (
      <label
        className={cn(
          'input m-2 flex h-10 min-w-0 items-center gap-2 border-none bg-base-100 text-sm has-[:focus]:border-none has-[:focus]:outline-none',
          className,
        )}>
        <div className='size-5'>
          <SearchNormal1 className={cn('size-5 text-secondary', { hidden: !icon })} />
        </div>
        <input
          ref={ref}
          type='text'
          id='search-input'
          autoFocus={autoFocus}
          className={cn('w-full grow bg-base-100', inputClassName)}
          value={keyword}
          enterKeyHint='search'
          placeholder={placeholder ?? t('search.placeholder')}
          onChange={(e) => {
            setKeyword?.(e.target.value);
            onChange?.(e);
          }}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete='off'
          autoCorrect='off'
          spellCheck='false'
          autoCapitalize='none'
        />
        {clearKeyword && keyword && (
          <button
            type='button'
            className='max-[320px]:hidden btn btn-circle btn-ghost btn-sm'
            onClick={clearKeyword}>
            <CloseCircle
              variant='Bold'
              className='size-5 text-secondary'
            />
          </button>
        )}
      </label>
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
